<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Left Column -->
      <div class="space-y-4">
        <!-- Personal Information Section -->
        <div class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3 flex items-center">
            <div class="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mr-2">
              <i class="fas fa-user text-primary-600 dark:text-primary-400 text-sm"></i>
            </div>
            Personal Information
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                First Name
                <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">
                  {{ !formData.company_name ? '(Required)' : '(Optional)' }}
                </span>
              </label>
              <input type="text" v-model="formData.first_name" :required="!formData.company_name"
                class="block w-full rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                placeholder="Enter first name" />
            </div>
            <div class="space-y-1">
              <label
                class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">Last
                Name</label>
              <input type="text" v-model="formData.last_name"
                class="block w-full rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                placeholder="Enter last name" />
            </div>
          </div>
        </div>

        <!-- Contact Information Section -->
        <div class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3 flex items-center">
            <div class="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mr-2">
              <i class="fas fa-address-book text-primary-600 dark:text-primary-400 text-sm"></i>
            </div>
            Contact Information
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Phone
                <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Optional)</span>
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fas fa-phone text-surface-400 text-xs"></i>
                </div>
                <input type="tel" v-model="formData.phone" @input="onPhoneInput" @blur="validatePhone"
                  class="block w-full pl-8 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                  :class="[
                    phoneError
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500 dark:border-red-500'
                      : 'border-surface-300 dark:border-surface-600'
                  ]" placeholder="09171234567" />
              </div>
              <p v-if="phoneError" class="text-xs text-red-600 dark:text-red-400 mt-1">{{ phoneError }}</p>
            </div>
            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Email
                <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Optional)</span>
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fas fa-envelope text-surface-400 text-xs"></i>
                </div>
                <input type="email" v-model="formData.email"
                  class="block w-full pl-8 rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                  @input="onEmailInput" @blur="validateEmail" :class="[
                    emailError
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500 dark:border-red-500'
                      : 'border-surface-300 dark:border-surface-600'
                  ]" placeholder="client@example.com" />
              </div>
              <p v-if="emailError" class="text-xs text-red-600 dark:text-red-400 mt-1">{{ emailError }}</p>
            </div>
          </div>
        </div>

        <!-- Company Information Section -->
        <div class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3 flex items-center">
            <div class="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mr-2">
              <i class="fas fa-building text-primary-600 dark:text-primary-400 text-sm"></i>
            </div>
            Company Information
          </h3>
          <div class="space-y-3">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-1">
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                  Company Name
                  <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">
                    {{ !formData.first_name ? '(Required)' : '(Optional)' }}
                  </span>
                </label>
                <input type="text" v-model="formData.company_name" :required="!formData.first_name"
                  class="block w-full rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                  placeholder="Enter company name" />
              </div>
              <div class="space-y-1">
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                  Tax ID
                  <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Optional)</span>
                </label>
                <input type="text" v-model="formData.tax_id"
                  class="block w-full rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                  placeholder="Enter tax ID" />
              </div>
            </div>

            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Withholding Tax Rate
                <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Optional) –</span>
                <span class="text-[10px] font-normal text-orange-600 dark:text-orange-400 ml-1">Requires Admin
                  Approval</span>
              </label>
              <div class="relative">
                <input type="number" v-model.number="formData.withholding_tax_rate" min="0" max="100" step="0.01"
                  placeholder="0.00"
                  class="block w-full rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2 pr-8 border-surface-300 dark:border-surface-600" />
                <span
                  class="absolute right-3 top-1/2 transform -translate-y-1/2 text-surface-500 dark:text-surface-400 pointer-events-none">%</span>
              </div>
              <p class="text-[10px] text-surface-500 dark:text-surface-400 mt-1">
                Tax rate deducted by client (e.g., 2% for withholding tax). Used to calculate expected payment.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="space-y-4">
        <!-- Settings & Preferences Section -->
        <div class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3 flex items-center">
            <div class="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mr-2">
              <i class="fas fa-cog text-primary-600 dark:text-primary-400 text-sm"></i>
            </div>
            Settings & Preferences
          </h3>
          <div class="space-y-3">
            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Classification
                <span v-if="!canEditClassificationAndPayment"
                  class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Admin Only)</span>
              </label>
              <div class="flex items-center gap-2">
                <select v-model="formData.classification" :disabled="!canEditClassificationAndPayment"
                  class="flex-1 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 text-sm px-3 py-2"
                  :class="{
                    'border-surface-300 dark:border-surface-600': canEditClassificationAndPayment,
                    'border-surface-200 bg-surface-50 text-surface-500 cursor-not-allowed dark:border-surface-600 dark:bg-surface-800 dark:text-surface-400': !canEditClassificationAndPayment
                  }">
                  <option value="new">New</option>
                  <option value="regular">Regular</option>
                  <option value="vip">VIP</option>
                  <option value="inactive">Inactive</option>
                </select>

                <!-- Notification Preferences Button for Existing Clients -->
                <button v-if="editMode && props.client" type="button" @click="$emit('openPreferences')"
                  class="inline-flex items-center px-3 py-2 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors whitespace-nowrap"
                  title="Configure notifications">
                  <i class="fas fa-bell mr-1.5 text-xs"></i>
                  <span>Notifications</span>
                </button>
              </div>
            </div>
            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Payment Options
                <span class="text-[10px] font-normal text-orange-600 dark:text-orange-400 ml-1">(Requires Admin
                  Approval)</span>
              </label>
              <div
                class="flex items-center gap-4 p-2 bg-surface-50 dark:bg-surface-800/50 rounded-lg border border-surface-200 dark:border-surface-700">
                <label class="inline-flex items-center cursor-pointer">
                  <input type="checkbox" v-model="formData.pay_later"
                    class="rounded shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-primary-600 w-4 h-4" />
                  <span class="ml-2 text-sm text-surface-700 dark:text-surface-200">Allow Pay Later</span>
                </label>
                <label class="inline-flex items-center cursor-pointer">
                  <input type="checkbox" v-model="formData.purchase_order"
                    class="rounded shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-primary-600 w-4 h-4" />
                  <span class="ml-2 text-sm text-surface-700 dark:text-surface-200">Allow Purchase Order</span>
                </label>
              </div>
              <p class="text-[10px] text-surface-500 dark:text-surface-400 mt-1">
                Changes require admin approval.
              </p>
            </div>
          </div>
        </div>

        <!-- Additional Information Section -->
        <div class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3 flex items-center">
            <div class="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mr-2">
              <i class="fas fa-info-circle text-primary-600 dark:text-primary-400 text-sm"></i>
            </div>
            Additional Information
          </h3>
          <div class="space-y-3">
            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Address
                <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Optional)</span>
              </label>
              <textarea v-model="formData.address" rows="3"
                class="block w-full rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                placeholder="Enter client address"></textarea>
            </div>

            <div class="space-y-1">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 uppercase tracking-wide">
                Notes
                <span class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Optional)</span>
              </label>
              <textarea v-model="formData.notes" rows="3"
                class="block w-full rounded-lg border-surface-300 dark:border-surface-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 text-sm px-3 py-2"
                placeholder="Enter any additional notes"></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="flex justify-end space-x-3 pt-4 border-t border-surface-200 dark:border-surface-700">
      <button type="button" @click="$emit('cancel')"
        class="px-4 py-2 text-sm font-medium text-surface-700 dark:text-surface-200 bg-white dark:bg-surface-700 border border-surface-300 dark:border-surface-600 rounded-lg shadow-sm hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors">
        Cancel
      </button>
      <button type="submit" :disabled="checkingDuplicates"
        class="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg shadow-sm hover:bg-primary-700 dark:hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center">
        <i v-if="checkingDuplicates" class="fas fa-spinner fa-spin mr-2 text-xs"></i>
        <i v-else :class="editMode ? 'fas fa-save mr-2 text-xs' : 'fas fa-plus mr-2 text-xs'"></i>
        {{ editMode ? 'Update' : 'Create' }} Client
      </button>
    </div>
  </form>

  <!-- Duplicate Warning Modal -->
  <div v-if="showDuplicateWarning"
    class="fixed inset-0 bg-gray-900/50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
    <div class="relative w-11/12 md:w-2/3 lg:w-1/2 bg-white dark:bg-dark-800 shadow-2xl rounded-xl border border-gray-200 dark:border-dark-700">
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100 flex items-center">
            <i class="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
            Potential Duplicate Client Found
          </h3>
          <button @click="cancelCreation"
            class="text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Warning Message -->
        <div
          class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <p class="text-sm text-yellow-800 dark:text-yellow-200">
            We found {{ duplicateClients.length }} existing client(s) that might be similar.
            Please review below to avoid duplicates.
          </p>
        </div>

        <!-- Duplicate Clients List -->
        <div class="mb-6 max-h-64 overflow-y-auto pr-2">
          <div v-for="(duplicate, index) in duplicateClients" :key="duplicate.id"
            class="border border-surface-200 dark:border-surface-600 rounded-lg p-3 mb-3 bg-surface-50 dark:bg-surface-800/50 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors">
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h4 class="font-bold text-surface-900 dark:text-surface-100">
                  {{ duplicate.display_name || `${duplicate.first_name} ${duplicate.last_name}`.trim() ||
                    duplicate.company_name || `Client #${duplicate.id}` }}
                </h4>
                <div class="mt-2 text-sm text-surface-600 dark:text-surface-300 space-y-1.5">
                  <div v-if="duplicate.email" class="flex items-center">
                    <i class="fas fa-envelope w-4 mr-2 text-surface-400"></i>
                    {{ duplicate.email }}
                  </div>
                  <div v-if="duplicate.phone" class="flex items-center">
                    <i class="fas fa-phone w-4 mr-2 text-surface-400"></i>
                    {{ duplicate.phone }}
                  </div>
                  <div v-if="duplicate.company_name" class="flex items-center">
                    <i class="fas fa-building w-4 mr-2 text-surface-400"></i>
                    {{ duplicate.company_name }}
                  </div>
                  <div class="flex items-center mt-2 pt-2 border-t border-surface-200 dark:border-surface-700">
                    <span class="px-2 py-0.5 text-xs font-semibold rounded-full bg-opacity-20" :class="{
                      'bg-yellow-100 text-yellow-800': duplicate.classification === 'vip',
                      'bg-green-100 text-green-800': duplicate.classification === 'regular',
                      'bg-blue-100 text-blue-800': duplicate.classification === 'new',
                      'bg-surface-100 text-surface-800': !duplicate.classification
                    }">
                      {{ duplicate.classification?.toUpperCase() || 'UNKNOWN' }}
                    </span>
                    <span class="ml-auto text-xs text-surface-500">
                      Created: {{ new Date(duplicate.created_at).toLocaleDateString() }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-surface-200 dark:border-surface-700">
          <button @click="cancelCreation"
            class="px-4 py-2 text-sm font-medium text-surface-700 dark:text-surface-200 bg-white dark:bg-surface-700 border border-surface-300 dark:border-surface-600 rounded-lg shadow-sm hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors">
            Cancel
          </button>
          <button @click="proceedWithCreation"
            class="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg shadow-sm hover:bg-primary-700 dark:hover:bg-primary-500 transition-colors">
            Create Anyway
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useToast } from '@/composables/useToast';
import { useAuthStore } from '@/stores/auth';
import { clientsApi } from '@/services/api';

const props = defineProps({
  client: {
    type: Object,
    default: () => null
  }
});

const emit = defineEmits(['submit', 'cancel', 'error', 'openPreferences']);

const toast = useToast();
const authStore = useAuthStore();

// Check if user can edit classification and payment options
const canEditClassificationAndPayment = computed(() => {
  const userRole = authStore.user?.role?.toLowerCase();
  return ['super_admin', 'admin', 'manager'].includes(userRole);
});

const editMode = ref(false);
const formData = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company_name: '',
  address: '',
  classification: 'new', // Default to 'new'
  tax_id: '',
  withholding_tax_rate: 0, // Default to 0% (no tax)
  notes: '',
  is_active: true, // Default to active
  pay_later: false, // Add new field with default false
  purchase_order: false, // Add new field with default false
  allow_sms_updates: false, // Add new field with default false
  allow_email_updates: false, // Add new field with default false
  notification_preferences: {
    sms: {
      order_status_updates: false,
      invoice_and_receipts: false,
      quotations_and_approvals: false,
      payments_and_balance: false,
      marketing_and_reviews: false,
      soa_and_statements: false
    },
    email: {
      order_status_updates: false,
      invoice_and_receipts: false,
      quotations_and_approvals: false,
      payments_and_balance: false,
      marketing_and_reviews: false,
      soa_and_statements: false
    }
  }
});

// Phone & email validation state
const phoneError = ref('');
const emailError = ref('');

// Normalize names to title case before validation/submission
// Fixed to handle special characters like ñ correctly - doesn't capitalize letters after ñ
const normalizeName = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  // Convert to lowercase first
  const lowercased = trimmed.toLowerCase();

  // Split by spaces and hyphens, then capitalize first letter of each word part
  // This preserves special characters like ñ and doesn't capitalize letters after them
  return lowercased
    .split(/([\s-]+)/) // Split by spaces and hyphens, keeping the delimiters
    .map((part) => {
      // Keep delimiter parts (spaces/hyphens) as-is
      if (/^[\s-]+$/.test(part)) {
        return part;
      }
      // For word parts, capitalize only the first character if it's a letter
      // This preserves special characters like ñ in the middle of words
      if (part.length > 0) {
        const firstChar = part[0];
        // Only capitalize if the first character is a letter (including accented letters)
        if (/[a-záéíóúñü]/.test(firstChar)) {
          return firstChar.toUpperCase() + part.slice(1);
        }
      }
      return part;
    })
    .join('');
};

// Duplicate checking state
const checkingDuplicates = ref(false);
const duplicateClients = ref([]);
const showDuplicateWarning = ref(false);

// Allow only digits, parentheses, dashes, and leading '+' - remove all whitespaces
const onPhoneInput = (event) => {
  const value = event.target.value;
  // Remove all whitespaces and keep only allowed characters (digits, +, -, parentheses)
  const cleaned = value.replace(/\s/g, '').replace(/[^0-9+\-()]/g, '');
  if (cleaned !== value) {
    formData.value.phone = cleaned;
  }
  // Clear error while typing
  phoneError.value = '';
};

// Remove all whitespaces from email input
const onEmailInput = (event) => {
  const value = event.target.value;
  // Remove all whitespaces from email
  const cleaned = value.replace(/\s/g, '');
  if (cleaned !== value) {
    formData.value.email = cleaned;
  }
  // Clear error while typing
  emailError.value = '';
};

// Validate Philippine mobile format: 08XXXXXXXXX, 09XXXXXXXXX or +638XXXXXXXXX, +639XXXXXXXXX
const validatePhone = () => {
  const raw = formData.value.phone?.trim();
  if (!raw) {
    phoneError.value = '';
    return true;
  }
  const normalized = raw.replace(/[\s\-()]/g, '');
  const isValid = /^(\+63|0)[89]\d{9}$/.test(normalized);
  phoneError.value = isValid ? '' : 'Enter a valid PH mobile number (e.g., 08171234567, 09171234567 or +638171234567, +639171234567)';
  return isValid;
};

const validateEmail = () => {
  const raw = formData.value.email?.trim();
  if (!raw) {
    emailError.value = '';
    return true;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const ok = emailRegex.test(raw);
  emailError.value = ok ? '' : 'Enter a valid email address (e.g., name@example.com)';
  return ok;
};

const isValidPhone = computed(() => {
  const raw = formData.value.phone?.trim();
  if (!raw) return false;
  const normalized = raw.replace(/[\s\-()]/g, '');
  return /^(\+63|0)[89]\d{9}$/.test(normalized);
});

const isValidEmail = computed(() => {
  const raw = formData.value.email?.trim();
  if (!raw) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(raw);
});

// Duplicate checking function
const checkForDuplicates = async () => {
  if (editMode.value) return; // Skip duplicate check for editing

  try {
    checkingDuplicates.value = true;

    const checkData = {
      first_name: formData.value.first_name?.trim(),
      last_name: formData.value.last_name?.trim(),
      email: formData.value.email?.trim(),
      phone: formData.value.phone?.trim(),
      company_name: formData.value.company_name?.trim()
    };

    // Only check if we have meaningful data
    const hasData = Object.values(checkData).some(value => value && value.length > 0);
    if (!hasData) {
      return;
    }

    const response = await clientsApi.checkDuplicate(checkData);

    if (response.data?.hasDuplicates) {
      duplicateClients.value = response.data.duplicates;
      showDuplicateWarning.value = true;
    }
  } catch (error) {
    console.error('Error checking for duplicates:', error);
    // Don't show error to user, just log it
  } finally {
    checkingDuplicates.value = false;
  }
};

// Function to proceed with creation despite duplicates
const proceedWithCreation = () => {
  showDuplicateWarning.value = false;
  duplicateClients.value = [];
  emit('submit', formData.value);
};

// Function to cancel creation when duplicates found
const cancelCreation = () => {
  showDuplicateWarning.value = false;
  duplicateClients.value = [];
  emit('cancel');
};

onMounted(() => {
  if (props.client) {
    editMode.value = true;
    formData.value = {
      first_name: props.client.first_name,
      last_name: props.client.last_name,
      email: props.client.email || '',
      phone: props.client.phone || '',
      company_name: props.client.company_name || '',
      address: props.client.address || '',
      classification: props.client.classification || 'new',
      tax_id: props.client.tax_id || '',
      withholding_tax_rate: props.client.withholding_tax_rate || 0,
      notes: props.client.notes || '',
      is_active: props.client.is_active !== false, // Default to true unless explicitly false
      pay_later: props.client.pay_later || false,
      purchase_order: props.client.purchase_order || false,
      allow_sms_updates: props.client.allow_sms_updates || false,
      allow_email_updates: props.client.allow_email_updates || false,
      notification_preferences: props.client.notification_preferences || {
        sms: {
          order_status_updates: false,
          invoice_and_receipts: false,
          quotations_and_approvals: false,
          payments_and_balance: false,
          marketing_and_reviews: false,
          soa_and_statements: false
        },
        email: {
          order_status_updates: false,
          invoice_and_receipts: false,
          quotations_and_approvals: false,
          payments_and_balance: false,
          marketing_and_reviews: false,
          soa_and_statements: false
        }
      }
    };
  }
});

// Add watchers to update required status dynamically
watch(() => formData.value.first_name, (newVal) => {
  // Force Vue to re-render the component
  formData.value = { ...formData.value };
});

watch(() => formData.value.company_name, (newVal) => {
  // Force Vue to re-render the component
  formData.value = { ...formData.value };
});

const handleSubmit = async () => {
  try {
    // Auto-fix common capslock/spacing issues for names
    formData.value.first_name = normalizeName(formData.value.first_name);
    formData.value.last_name = normalizeName(formData.value.last_name);
    formData.value.company_name = normalizeName(formData.value.company_name);

    // Validate that we have at least one form of identification
    const hasFirstName = !!formData.value.first_name.trim();
    const hasLastName = !!formData.value.last_name.trim();
    const hasCompanyName = !!formData.value.company_name?.trim();

    // Check if we have a valid identification combination
    if ((!hasFirstName && !hasCompanyName) ||
      (hasLastName && !hasFirstName)) {
      toast.error('Please provide either First Name, First Name and Last Name, or Company Name');
      return;
    }

    // Phone validation only if provided
    if (!validatePhone()) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Email validation only if provided
    if (formData.value.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.value.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    // Format credit limit to number if it exists
    if (formData.value.credit_limit) {
      formData.value.credit_limit = Number(formData.value.credit_limit);
    }

    // Always set classification to 'new' for new clients
    if (!editMode.value) {
      formData.value.classification = 'new';
    }

    // Always ensure is_active is true for new clients
    if (!editMode.value) {
      formData.value.is_active = true;
    }

    // Check for duplicates before creating new client
    if (!editMode.value) {
      await checkForDuplicates();
      // If duplicates found, the warning modal will be shown
      // and the user can choose to proceed or cancel
      if (showDuplicateWarning.value) {
        return;
      }
    }

    // Only emit the submit event, let parent handle success notification
    emit('submit', formData.value);
  } catch (error) {
    console.error('Error in client form:', error);
    // Let the error propagate to parent
    throw error;
  }
};
</script>